<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Payout</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new payout record for users
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-plus</v-icon>
                      New Payout Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Payouts
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Payout Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cash</v-icon>
                    Payout Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.position_filter"
                        :items="positions"
                        item-text="text"
                        item-value="value"
                        label="Select Position"
                        placeholder="Choose position to filter users"
                        outlined
                        :rules="[(v) => !!v || 'Position is required']"
                        :error-messages="errors.position_filter"
                        class="professional-field"
                        hide-details="auto"
                        required
                        @change="onPositionChange"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account-group</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="filteredUsers"
                        item-text="text"
                        item-value="value"
                        label="Select User"
                        placeholder="Choose user to receive payout"
                        outlined
                        :rules="[(v) => !!v || 'User is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        :loading="loadingUsers"
                        :disabled="!form.position_filter"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                        <template #item="{ item }">
                          <v-list-item-avatar>
                            <v-avatar size="32" color="primary">
                              <span class="white--text text-caption">{{
                                getInitials(item.text)
                              }}</span>
                            </v-avatar>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              item.text
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.amount"
                        label="Payout Amount"
                        placeholder="Enter payout amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0.01"
                        :rules="amountRules"
                        :error-messages="errors.amount"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.payment_method"
                        :items="paymentMethods"
                        item-text="text"
                        item-value="value"
                        label="Payment Method"
                        placeholder="Select payment method"
                        outlined
                        :error-messages="errors.payment_method"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-credit-card</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.payout_status"
                        :items="payoutStatuses"
                        item-text="text"
                        item-value="value"
                        label="Payout Status"
                        placeholder="Select payout status"
                        outlined
                        :error-messages="errors.payout_status"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-cash-check</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Transaction Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-receipt</v-icon>
                    Transaction Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.transaction_id"
                        label="Transaction ID (Optional)"
                        placeholder="Enter transaction ID from payment gateway"
                        outlined
                        :error-messages="errors.transaction_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-identifier</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="datePickerMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            :value="formattedPaymentDateTime"
                            label="Payment Date & Time (Optional)"
                            prepend-inner-icon="mdi-calendar-clock"
                            outlined
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.payment_date"
                            class="professional-field"
                            hide-details="auto"
                            clearable
                            @click:clear="clearPaymentDate"
                          />
                        </template>
                        <v-card>
                          <v-card-text class="pa-0">
                            <v-tabs v-model="dateTimeTab" fixed-tabs>
                              <v-tab key="date">
                                <v-icon>mdi-calendar</v-icon>
                                Date
                              </v-tab>
                              <v-tab key="time" :disabled="!form.payment_date">
                                <v-icon>mdi-clock</v-icon>
                                Time
                              </v-tab>
                            </v-tabs>
                            <v-tabs-items v-model="dateTimeTab">
                              <v-tab-item key="date">
                                <v-date-picker
                                  v-model="form.payment_date"
                                  no-title
                                  scrollable
                                  color="primary"
                                  @input="dateTimeTab = 1"
                                />
                              </v-tab-item>
                              <v-tab-item key="time">
                                <v-time-picker
                                  v-if="dateTimeTab === 1 && form.payment_date"
                                  v-model="form.payment_time"
                                  no-title
                                  color="primary"
                                  format="24hr"
                                />
                              </v-tab-item>
                            </v-tabs-items>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer />
                            <v-btn text @click="datePickerMenu = false"
                              >Cancel</v-btn
                            >
                            <v-btn
                              text
                              color="primary"
                              @click="datePickerMenu = false"
                              :disabled="!form.payment_date"
                            >
                              OK
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.remarks"
                        label="Remarks (Optional)"
                        placeholder="Enter remarks or notes about this payout"
                        outlined
                        rows="3"
                        :error-messages="errors.remarks"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-note-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status Section -->
              </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Form Actions -->
            <v-card-actions class="form-actions py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Form Status -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="valid ? 'success' : 'warning'"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>{{
                        valid ? "mdi-check-circle" : "mdi-alert-circle"
                      }}</v-icon>
                      {{ valid ? "Form Valid" : "Form Invalid" }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{
                        valid
                          ? "Ready to submit"
                          : "Please complete required fields"
                      }}
                    </span>
                  </div>
                </v-col>

                <!-- Right Side: Action Buttons -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      outlined
                      color="grey darken-1"
                      @click="resetForm"
                      class="mr-2"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Reset
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click="submitForm"
                      :loading="loading"
                      :disabled="!valid"
                      class="submit-button"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Create Payout
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import { combineIstDateTime } from "@/utils/dateTime";

export default {
  name: "PayoutCreate",
  data() {
    return {
      valid: false,
      loading: false,
      loadingUsers: false,

      // Date/Time picker
      datePickerMenu: false,
      dateTimeTab: 0,

      form: {
        position_filter: null,
        user_id: null,
        amount: null,
        payment_method: null,
        payout_status: 0, // Default to Pending (backend expects 0)
        transaction_id: "",
        payment_date: null,
        payment_time: null,
        remarks: "",
        is_active: true,
      },

      errors: {},

      // Dropdown options
      positions: [],
      users: [],
      filteredUsers: [],
      paymentMethods: [],
      payoutStatuses: [],

      amountRules: [
        (v) => !!v || "Amount is required",
        (v) => !isNaN(parseFloat(v)) || "Amount must be a valid number",
        (v) => parseFloat(v) > 0 || "Amount must be greater than 0",
        (v) => parseFloat(v) <= 999999.99 || "Amount cannot exceed 999,999.99",
      ],
    };
  },

  computed: {
    formattedPaymentDateTime() {
      if (!this.form.payment_date) return "";

      // payment_date is a plain YYYY-MM-DD string; parse/format in UTC so the
      // displayed day matches what was picked regardless of browser timezone.
      const date = new Date(`${this.form.payment_date}T00:00:00Z`);
      let formatted = date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });

      if (this.form.payment_time) {
        formatted += ` at ${this.form.payment_time}`;
      }

      return formatted;
    },

    combinedDateTime() {
      return combineIstDateTime(this.form.payment_date, this.form.payment_time);
    },

    isSeller() {
      return this.user && this.user.position && this.user.position.name
        ? this.user.position.name.toUpperCase() === 'SELLER'
        : false;
    },
  },

  mounted() {
    // Redirect sellers - they cannot create payouts
    if (this.isSeller) {
      this.$store.dispatch("errors/showError", "You do not have permission to create payouts");
      this.$router.push("/payouts");
      return;
    }
    this.getDropdownData();
  },

  methods: {
    async getDropdownData() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/masters", {
          params: { context: "payouts" },
        });

        // Store all users for filtering
        this.users = response.data.users.map((user) => ({
          text: `${user.first_name} ${user.last_name} (${user.email})`,
          value: user.id,
          position_id: user.position_id,
          position_name: user.position?.name || 'No Position',
        }));

        // Get unique positions from users
        this.positions = this.getUniquePositions(response.data.users);

        // Map payment methods to dropdown format with backend-expected values
        this.paymentMethods = response.data.payment_methods.map((method, index) => ({
          text: method.description,
          value: index + 1, // Backend expects integers 1-10
          originalId: method.id,
        }));

        // Map payout statuses to dropdown format with backend-expected values
        const statusMapping = {
          'Pending': 0,
          'Completed': 1,
          'Failed': 2,
          'Processing': 0, // Map to Pending for now
          'Cancelled': 2, // Map to Failed 
          'On Hold': 0, // Map to Pending
          'Rejected': 2, // Map to Failed
          'Approved': 1, // Map to Completed
          'Under Review': 0 // Map to Pending
        };
        
        this.payoutStatuses = response.data.payout_statuses
          .filter(status => Object.prototype.hasOwnProperty.call(statusMapping, status.description))
          .map((status) => ({
            text: status.description,
            value: statusMapping[status.description],
            originalId: status.id,
          }));

        // Remove duplicates based on value
        const uniqueStatuses = [];
        const seenValues = new Set();
        this.payoutStatuses.forEach(status => {
          if (!seenValues.has(status.value)) {
            seenValues.add(status.value);
            uniqueStatuses.push(status);
          }
        });
        this.payoutStatuses = uniqueStatuses;

        // Set default payout status to "Pending" (value 0)
        if (!this.form.payout_status && this.form.payout_status !== 0) {
          this.form.payout_status = 0;
        }
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    getUniquePositions(users) {
      const positionsMap = new Map();
      
      users.forEach(user => {
        if (user.position && user.position.id && user.position.name) {
          // Only include Doctor and Seller positions
          const positionName = user.position.name.toLowerCase();
          if (positionName.includes('doctor') || positionName.includes('seller')) {
            positionsMap.set(user.position.id, {
              value: user.position.id,
              text: user.position.name
            });
          }
        }
      });
      
      return Array.from(positionsMap.values());
    },

    onPositionChange() {
      // Reset user selection when position changes
      this.form.user_id = null;
      
      // Filter users based on selected position
      if (this.form.position_filter) {
        this.filteredUsers = this.users.filter(user => 
          user.position_id === this.form.position_filter
        );
      } else {
        this.filteredUsers = [];
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        // Prepare form data with combined date-time
        const formData = { ...this.form };
        if (this.combinedDateTime) {
          formData.payment_date = this.combinedDateTime;
        }

        // Remove fields that shouldn't be sent to backend
        delete formData.payment_time;
        delete formData.position_filter;

        await axios.post("/payouts", formData);

        this.$store.dispatch(
          "errors/showSuccess",
          "Payout created successfully!"
        );

        // Navigate back to the payout index page
        this.$router.push("/payouts");
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        }
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.$refs.form.reset();
      this.form = {
        position_filter: null,
        user_id: null,
        amount: null,
        payment_method: null,
        payout_status: 0,
        transaction_id: "",
        payment_date: null,
        payment_time: null,
        remarks: "",
        is_active: true,
      };
      this.filteredUsers = [];
      this.errors = {};
      this.dateTimeTab = 0;
    },

    clearPaymentDate() {
      this.form.payment_date = null;
      this.form.payment_time = null;
      this.dateTimeTab = 0;
    },

    getInitials(name) {
      if (!name) return "U";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
  },
};
</script>

<style scoped>
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.submit-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-chip {
  font-weight: 500;
}

.text-caption {
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .submit-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .submit-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>